import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAgentCatalog, getMyAgents } from '../lib/agents';
import oraculoBrujula from '../assets/images/oraculo-brujula.png';

/**
 * InicioHub — hub visual alineado al mock Ola A (02-inicio.html / after-inicio-dark.png).
 * Recibe saldos y callbacks del Dashboard; no toca TransferForm ni SendToUser v2.
 */

const FALLBACK_PRESETS = [
  { key: 'constante', nombre: 'El Constante', estrategia: 'Aporte periódico (DCA)', letter: 'C' },
  { key: 'paciente', nombre: 'El Paciente', estrategia: 'Tendencia', letter: 'P' },
  { key: 'cazador', nombre: 'El Cazador', estrategia: 'Comprar caídas', letter: 'Z' },
  { key: 'guardian', nombre: 'El Guardián', estrategia: 'Defensa del capital', letter: 'G' },
];

const ORACULO_SAMPLE = '¿Cómo va mi plata en dólares?';

function fmtCop(n) {
  const v = Number(n) || 0;
  return `$ ${Math.round(v).toLocaleString('es-CO')} COP`;
}

function fmtUsd(n) {
  const v = Number(n) || 0;
  return `$ ${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
}

function initialLetter(name) {
  const s = String(name || '').trim();
  return (s[0] || '?').toUpperCase();
}

function goOraculo(text, voice = false) {
  try {
    if (voice) localStorage.setItem('fp_oraculo_voice', '1');
    else if (text && text.trim()) localStorage.setItem('fp_oraculo_prefill', text.trim());
  } catch { /* localStorage bloqueado */ }
}

export default function InicioHub({
  firstName,
  balanceCop = 0,
  balanceUsdc = 0,
  balance = 0,
  dualBalanceEnabled = false,
  onConvert,
  onNavigate,
}) {
  const navigate = useNavigate();
  const go = onNavigate || ((path) => navigate(path));

  const [catalogo, setCatalogo] = useState([]);
  const [misAgentes, setMisAgentes] = useState([]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      getAgentCatalog().catch(() => null),
      getMyAgents().catch(() => []),
    ]).then(([cat, mios]) => {
      if (!alive) return;
      setCatalogo(cat?.catalogo || []);
      setMisAgentes(Array.isArray(mios) ? mios : []);
    });
    return () => { alive = false; };
  }, []);

  const displayName = firstName || 'tú';
  const usdShown = dualBalanceEnabled ? balanceUsdc : balance;
  const copShown = dualBalanceEnabled ? balanceCop : 0;

  const agentPills = useMemo(() => {
    const mine = (misAgentes || []).slice(0, 4).map((a) => ({
      id: `mine-${a.id}`,
      nombre: a.nombre || 'Tu bot',
      meta: a.estrategia || a.preset || 'Tu bot',
      letter: initialLetter(a.nombre),
      chip: 'Tu bot',
      chipBlue: false,
    }));
    if (mine.length >= 4) return mine;

    const usedPresets = new Set((misAgentes || []).map((a) => a.preset).filter(Boolean));
    const fromCat = (catalogo.length ? catalogo : FALLBACK_PRESETS)
      .filter((c) => !usedPresets.has(c.key))
      .map((c) => ({
        id: `preset-${c.key}`,
        nombre: c.nombre || c.key,
        meta: c.estrategia || c.descripcion || 'Preset',
        letter: initialLetter(c.nombre || c.key),
        chip: 'Preset',
        chipBlue: true,
      }));

    return [...mine, ...fromCat].slice(0, 4);
  }, [catalogo, misAgentes]);

  const openOraculo = (voice = false, text = '') => {
    goOraculo(text, voice);
    go('/dashboard/investments?tab=oraculo');
  };

  return (
    <div className="inicio-hub">
      <div className="inicio-hub__greet">
        <h1>Hola, {displayName}</h1>
        <p>
          Dinero en COP y dólares, envíos gratis entre Flowex, cuentas globales y agentes con
          estrategia clara. El Oráculo es overlay (voz), no un tab del menú.
        </p>
      </div>

      <div className="inicio-hub__balances">
        <div className="inicio-hub__bal">
          <div className="inicio-hub__bal-lbl">COP disponible</div>
          <div className="inicio-hub__bal-val">{dualBalanceEnabled ? fmtCop(copShown) : '—'}</div>
          <div className="inicio-hub__bal-note">Mi saldo · Colombia</div>
        </div>
        <div className="inicio-hub__bal inicio-hub__bal--usd">
          <div className="inicio-hub__bal-lbl">USD disponible</div>
          <div className="inicio-hub__bal-val">{fmtUsd(usdShown)}</div>
          <div className="inicio-hub__bal-note">Saldo en dólares</div>
        </div>
      </div>

      <div className="inicio-hub__actions">
        <button type="button" className="inicio-hub__btn inicio-hub__btn--p" onClick={() => go('/dashboard/recharge')}>
          Recargar PSE
        </button>
        <button type="button" className="inicio-hub__btn inicio-hub__btn--g" onClick={() => go('/dashboard/accounts')}>
          Cuentas Globales
        </button>
        <button type="button" className="inicio-hub__btn inicio-hub__btn--g" onClick={() => go('/dashboard/transfer')}>
          Enviar · P2P gratis
        </button>
        <button
          type="button"
          className="inicio-hub__btn inicio-hub__btn--g"
          onClick={() => (typeof onConvert === 'function' ? onConvert() : go('/dashboard/assets'))}
        >
          Pasar a dólares
        </button>
      </div>

      <div className="inicio-hub__ruta">
        <span className="inicio-hub__ruta-label">Ruta del dinero</span>
        <div className="inicio-hub__ruta-steps">
          <div className="inicio-hub__ruta-step"><span className="n">1</span> Recarga COP (PSE)</div>
          <span className="inicio-hub__ruta-arrow" aria-hidden="true">→</span>
          <div className="inicio-hub__ruta-step"><span className="n">2</span> Pasa a USD</div>
          <span className="inicio-hub__ruta-arrow" aria-hidden="true">→</span>
          <div className="inicio-hub__ruta-step"><span className="n">3</span> Invierte / envía</div>
        </div>
      </div>

      <div className="inicio-hub__grid3">
        <div className="inicio-hub__panel">
          <h3>Oráculo · overlay de voz</h3>
          <div className="inicio-hub__oraculo">
            <div className="inicio-hub__orb" aria-hidden="true">
              <img src={oraculoBrujula} alt="" className="inicio-hub__orb-img" />
            </div>
            <button type="button" className="inicio-hub__btn inicio-hub__btn--p" onClick={() => openOraculo(true)}>
              Háblale
            </button>
            <p className="inicio-hub__oraculo-copy">
              No está en el sidebar: se abre como overlay. Informa sobre tu plata; no inventa rendimientos.
            </p>
            <button
              type="button"
              className="inicio-hub__sample"
              onClick={() => openOraculo(false, ORACULO_SAMPLE)}
            >
              <span>Ejemplo</span>
              {ORACULO_SAMPLE}
            </button>
          </div>
        </div>

        <div className="inicio-hub__panel">
          <h3>Qué puedes hacer</h3>
          <button type="button" className="inicio-hub__folio" onClick={() => go('/dashboard/recharge')}>
            <div>
              <div className="name">Recargar PSE</div>
              <div className="meta">PSE · sin costo de recarga (UI)</div>
            </div>
          </button>
          <button type="button" className="inicio-hub__folio" onClick={() => go('/dashboard/accounts')}>
            <div>
              <div className="name">Cuentas Globales</div>
              <div className="meta">ACH / SEPA / SPEI / PIX</div>
            </div>
          </button>
          <button type="button" className="inicio-hub__folio" onClick={() => go('/dashboard/transfer')}>
            <div>
              <div className="name">P2P Flowex</div>
              <div className="meta">Email · inmediato · Gratis</div>
            </div>
          </button>
          <button type="button" className="inicio-hub__folio" onClick={() => go('/dashboard/metas')}>
            <div>
              <div className="name">Metas + WhatsApp</div>
              <div className="meta">Escuchar · Enviar a WA</div>
            </div>
          </button>
        </div>

        <div className="inicio-hub__panel">
          <h3>Invertir</h3>
          <div className="inicio-hub__folio">
            <div>
              <div className="name">Acciones y ETFs</div>
              <div className="meta">Práctica o invertido</div>
            </div>
            <button type="button" className="inicio-hub__btn inicio-hub__btn--g inicio-hub__btn--sm" onClick={() => go('/dashboard/investments')}>
              Ver
            </button>
          </div>
          <div className="inicio-hub__folio">
            <div>
              <div className="name">Mis Agentes</div>
              <div className="meta">4 presets · DCA / tendencia…</div>
            </div>
            <button type="button" className="inicio-hub__btn inicio-hub__btn--g inicio-hub__btn--sm" onClick={() => go('/dashboard/agentes')}>
              Ver
            </button>
          </div>
          <div className="inicio-hub__folio">
            <div>
              <div className="name">Mis Metas</div>
              <div className="meta">Cuota + mercado puso N</div>
            </div>
            <button type="button" className="inicio-hub__btn inicio-hub__btn--g inicio-hub__btn--sm" onClick={() => go('/dashboard/metas')}>
              Ver
            </button>
          </div>
        </div>
      </div>

      <div className="inicio-hub__agents">
        <div className="inicio-hub__agents-head">
          <h3>Agentes · teaser de estrategia</h3>
          <button type="button" className="inicio-hub__btn inicio-hub__btn--g inicio-hub__btn--sm" onClick={() => go('/dashboard/agentes')}>
            Mis Agentes
          </button>
        </div>
        <div className="inicio-hub__agents-row">
          {agentPills.map((a) => (
            <button
              key={a.id}
              type="button"
              className="inicio-hub__agent-pill"
              onClick={() => go('/dashboard/agentes')}
            >
              <div className="inicio-hub__avatar">{a.letter}</div>
              <div className="inicio-hub__agent-text">
                <div className="n">{a.nombre}</div>
                <div className="m">{a.meta}</div>
              </div>
              <span className={`inicio-hub__chip${a.chipBlue ? ' inicio-hub__chip--blue' : ''}`}>{a.chip}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="inicio-hub__tertiary">
        <button type="button" className="inicio-hub__tchip" onClick={() => go('/dashboard/metas')}>
          Mis Metas · WhatsApp
        </button>
        <button type="button" className="inicio-hub__tchip" onClick={() => go('/dashboard/capsulas')}>
          Cápsulas
        </button>
        <button type="button" className="inicio-hub__tchip" onClick={() => go('/dashboard/mapa')}>
          El mapa
        </button>
      </div>
    </div>
  );
}
